import { LuPackageSearch, LuCalendarClock, LuBellRing, LuUsers } from "react-icons/lu";
import { LuLayoutDashboard, LuBoxes, LuSettings } from "react-icons/lu";
import { RiFileChartLine } from "react-icons/ri";
import { DASHBOARD, GLOBAL_SEARCH, INVENTORY, ORGANIZATION, PROFILE, REQUESTS, SUPPLIER, TOOLS, USERS } from "../routes/RouteConstant";
import { GoOrganization } from "react-icons/go";
import { TbWorldSearch } from "react-icons/tb";
import {
  FaCut, FaDna, FaThermometerHalf, FaLink, FaExchangeAlt,
  FaFlask, FaAtlas, FaCubes, FaCode, FaSearch, FaProjectDiagram, FaTree, FaShieldAlt, FaBookOpen, FaMicroscope,
} from "react-icons/fa";

export const VIEW = { LOGIN: "login", FORGOT: "forgot", REGISTER: "register" };

export const VIEW_COPY = {
  [VIEW.LOGIN]: {
    title: "Login to your LIMS account",
    subtitle: "Track samples, results and workflows in one place.",
  },
  [VIEW.FORGOT]: {
    title: "Reset your password",
    subtitle: "We'll email you a secure reset token.",
  },
  [VIEW.REGISTER]: {
    title: "Create a new organization",
    subtitle: "Set up your lab's workspace in a couple of minutes.",
  },
};


export const POINTS = [
  {
    icon: LuPackageSearch,
    title: "Know your stock, instantly",
    desc: "Every reagent and consumable visible in one place — no more guessing what's on the shelf.",
  },
  {
    icon: LuCalendarClock,
    title: "Never miss an expiry",
    desc: "Track expiry dates automatically and cut down on wasted, unusable stock.",
  },
  {
    icon: LuBellRing,
    title: "Reorder before you run out",
    desc: "Get flagged the moment something dips below its reorder point.",
  },
  {
    icon: LuUsers,
    title: "One shared source of truth",
    desc: "Your whole lab team works off the same live inventory — no spreadsheets, no confusion.",
  },
];


export function loginErrorToMessage(error) {
  if (!error) return "";
  if (!error.message && Array.isArray(error) && error.length > 0) {
    return error.at(-1);
  }
  switch (error.message) {
    case "Invalid user email":
      return "This user has not been registered yet. Please contact the administrator.";
    case "Account disabled":
      return "Your account is disabled. Please contact the administrator.";
    case "Account temporarily locked":
      return `Your account is temporarily locked until ${new Date(
        error.lockedUntil,
      ).toLocaleString()}. Please try again later or contact the administrator.`;
    case "Password not set":
      return 'Your account does not have a password set. Use the token sent to your email to set your password. If you have not received a token or the token has expired, click "Forgot password" to request a new one.';
    case "User organization has been deactivated":
      return "Your organization has been deactivated. It needs to be reactivated to access the system.";
    case "Invalid password":
      return error.failedAttempts < 5
        ? `Invalid password. You have ${5 - error.failedAttempts} attempt(s) left before your account is locked.`
        : "Your account has been locked for 24 hours due to too many failed login attempts.";
    default:
      return "An unexpected error occurred. Please try again later.";
  }
}



export const baseClasses =
  "w-full rounded-md border bg-white px-3.5 py-2.5 text-sm text-slate-900 " +
  "placeholder:text-slate-400 outline-none transition " +
  "focus:border-[#3652FF] focus:ring-4 focus:ring-[#3652FF]/10";


export const EMPTY_FORM = {
  orgName: "",
  userName: "",
  email: "",
  contactNumber: "",
  designation: "",
  address: "",
  taxId: "",
};


export const BINS = [
  { loc: "A-01", item: "Ethanol 95%", pct: 82, status: "ok" },
  { loc: "A-02", item: "Acetonitrile", pct: 14, status: "low" },
  { loc: "A-03", item: "Nitrile Gloves M", pct: 91, status: "ok" },
  { loc: "B-01", item: "PCR Master Mix", pct: 38, status: "expiring" },
  { loc: "B-02", item: "Pipette Tips 200µL", pct: 63, status: "ok" },
  { loc: "B-03", item: "Sodium Chloride", pct: 8, status: "critical" },
  { loc: "C-01", item: "Agar Plates", pct: 55, status: "ok" },
  { loc: "C-02", item: "Buffer Solution", pct: 27, status: "low" },
];

export const STATUS = {
  ok: { bar: "bg-emerald-500", ring: "ring-emerald-500/20", dot: "bg-emerald-500" },
  low: { bar: "bg-amber-500", ring: "ring-amber-500/20", dot: "bg-amber-500" },
  expiring: { bar: "bg-amber-500", ring: "ring-amber-500/20", dot: "bg-amber-500" },
  critical: { bar: "bg-rose-500", ring: "ring-rose-500/20", dot: "bg-rose-500" },
};

export const NAV_ITEMS = [
  { to: DASHBOARD, label: "Dashboard", icon: LuLayoutDashboard, end: true },
  { to: INVENTORY, label: "Inventory", icon: LuBoxes },
  { to: REQUESTS, label: "Requests", icon: LuPackageSearch },
  { to: SUPPLIER, label: "Supplier", icon: LuBellRing },
  { to: USERS, label: "Users", icon: RiFileChartLine, adminOnly: true },
  { to: TOOLS, label: "Tools", icon: LuUsers },
  { to: GLOBAL_SEARCH, label: "Google Search", icon: TbWorldSearch  },
  { to: PROFILE, label: "Profile", icon: LuSettings },
  { to: ORGANIZATION, label: "Organization", icon: GoOrganization , adminOnly: true},
];


export const ROLE_OPTIONS = [
  { value: "all", label: "All roles" },
  { value: "admin", label: "Admin" },
  { value: "member", label: "Member" },
];

export const STATUS_OPTIONS = [
  { value: "all", label: "All status" },
  { value: "active", label: "Active" },
  { value: "disabled", label: "Disabled" },
  { value: "locked", label: "Locked" },
];


export const REGEX = {
  text: /^[A-Za-z0-9][A-Za-z0-9 .,&()/_-]*$/,
  catalog: /^[A-Za-z0-9][A-Za-z0-9._/-]*$/,
  positiveNumber: /^(?:\d+|\d*\.\d+)$/,
  positiveInteger: /^[1-9]\d*$/,
};

/*
 * Required text validation
 */
export const validateRequired = (
  value,
  fieldName
) => {
  if (!value || !String(value).trim()) {
    return `${fieldName} is required.`;
  }

  return "";
};

/*
 * Text validation
 */
export const validateText = (
  value,
  fieldName
) => {
  const requiredError = validateRequired(
    value,
    fieldName
  );

  if (requiredError) {
    return requiredError;
  }

  if (!REGEX.text.test(String(value).trim())) {
    return `${fieldName} contains invalid characters.`;
  }

  return "";
};

/*
 * Catalog validation
 */
export const validateCatalog = (
  value,
  fieldName = "Catalog"
) => {
  const requiredError = validateRequired(
    value,
    fieldName
  );

  if (requiredError) {
    return requiredError;
  }

  if (
    !REGEX.catalog.test(
      String(value).trim()
    )
  ) {
    return `${fieldName} contains invalid characters.`;
  }

  return "";
};


export const validatePositiveNumber = (
  value,
  fieldName
) => {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return `${fieldName} is required.`;
  }

  const stringValue = String(value).trim();

  if (!REGEX.positiveNumber.test(stringValue)) {
    return `Enter a valid ${fieldName.toLowerCase()}.`;
  }

  if (Number(value) <= 0) {
    return `${fieldName} must be greater than 0.`;
  }

  return "";
};


export const validatePositiveInteger = (
  value,
  fieldName
) => {
  if (
    value === "" ||
    value === null ||
    value === undefined
  ) {
    return `${fieldName} is required.`;
  }

  const stringValue = String(value).trim();

  if (!REGEX.positiveInteger.test(stringValue)) {
    return `Enter a valid ${fieldName.toLowerCase()}.`;
  }

  return "";
};

/*
 * Optional text validation
 */
export const validateOptionalText = (
  value,
  fieldName,
  maxLength = 500
) => {
  if (!value || !String(value).trim()) {
    return "";
  }

  const stringValue = String(value).trim();

  if (stringValue.length > maxLength) {
    return `${fieldName} cannot exceed ${maxLength} characters.`;
  }

  if (!REGEX.text.test(stringValue)) {
    return `${fieldName} contains invalid characters.`;
  }

  return "";
};


// ------------------------------------------------------------------------
// Presentation helpers only — no behavior lives here.
// ------------------------------------------------------------------------

export const relativeDate = (dateStr) => {
  const itemDate = new Date(dateStr);
  const diff = Date.now() - itemDate;
  const minutes = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);
  if (minutes < 60) return `${minutes} ${minutes === 1 ? "minute" : "minutes"} ago`;
  if (hours < 24) return `${hours} ${hours === 1 ? "hour" : "hours"} ago`;
  return `${itemDate.toLocaleDateString()} (${days} ${days === 1 ? "day" : "days"} ago)`;
};
 
export const SUPPLIER_DOTS = ["bg-indigo-500", "bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-teal-500"];
export const SUPPLIER_THEMES = [
  { dot: "bg-indigo-500", hoverBorder: "hover:border-indigo-300", hoverShadow: "hover:shadow-indigo-200/70" },
  { dot: "bg-sky-500", hoverBorder: "hover:border-sky-300", hoverShadow: "hover:shadow-sky-200/70" },
  { dot: "bg-emerald-500", hoverBorder: "hover:border-emerald-300", hoverShadow: "hover:shadow-emerald-200/70" },
  { dot: "bg-amber-500", hoverBorder: "hover:border-amber-300", hoverShadow: "hover:shadow-amber-200/70" },
  { dot: "bg-rose-500", hoverBorder: "hover:border-rose-300", hoverShadow: "hover:shadow-rose-200/70" },
  { dot: "bg-teal-500", hoverBorder: "hover:border-teal-300", hoverShadow: "hover:shadow-teal-200/70" },
];
 
const STATUS_STYLES = {
  new: "bg-blue-50 text-blue-700 ring-blue-200",
  pending: "bg-amber-50 text-amber-700 ring-amber-200",
  approved: "bg-emerald-50 text-emerald-700 ring-emerald-200",
  rejected: "bg-rose-50 text-rose-700 ring-rose-200",
  ordered: "bg-violet-50 text-violet-700 ring-violet-200",
};
const statusClass = (status) =>
  STATUS_STYLES[status?.toLowerCase?.()] ?? "bg-gray-100 text-gray-600 ring-gray-200";
 
export const StatusBadge = ({ status }) => (
  <span
    className={`inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium capitalize ring-1 ring-inset ${statusClass(status)}`}
  >
    {status}
  </span>
);
 

export const Field = ({ label, children, className = "" }) => (
  <div className={`min-w-0 ${className}`}>
    <p className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">{label}</p>
    <p className="mt-1 truncate text-[13.5px] font-semibold text-gray-800">{children}</p>
  </div>
);
 


export const BLANK_PARTY = { name: "", email: "", contactNumber: "", person: "", address: "", taxId: "" };
 
export const DEFAULT_TERMS = [
  "The vendor is responsible for ensuring timely delivery as per the agreed quotation and phone discussions.",
  "All other conditions remain as per the quotation.",
  "Products must meet acceptable quality standards; otherwise, they must be replaced.",
  "The vendor will make every effort to expedite the delivery of urgent consumables as per the pre-purchase discussions.",
  "Provide GST invoice and other product-related documents at the time of delivery.",
  "During the warranty period of the products, prompt response is expected for any repair, maintenance, or services as required.",
];
 
export const PAYMENT_TERMS = [
  { value: "A", label: "50% payment in advance, 50% payment upon delivery" },
  { value: "B", label: "100% payment in advance" },
  { value: "C", label: "100% payment after completion of work or delivery" },
  { value: "D", label: "Payment as per pre-purchase discussions" },
];
 
export const PROJECTS = [
  { name: "Therapeutic Antibody", id: "MA-23" },
  { name: "Bispecific Antibody", id: "MA-23-25" },
  { name: "Spinal Cord Injury", id: "SCI-23" },
  { name: "Cancer Treatment", id: "CT-23" },
  { name: "Holistic Treatment", id: "CT-23-24A" },
  { name: "CAR-T Cell", id: "CT-23-24B" },
  { name: "Viral Engineering", id: "CT-23-24C" },
  { name: "Ligand based CAR-T Cell", id: "CT-23-25" },
  { name: "Mammalian Expression System", id: "MES-23" },
  { name: "Recombinant Vector", id: "MES-23A" },
  { name: "CHO Cells", id: "MES-23B" },
  { name: "Vaccine Design", id: "CT" },
  { name: "Neuronal Degeneration and Regeneration", id: "NDR" },
  { name: "Drug Delivery", id: "DD" },
  { name: "Topical Formulation", id: "TF" },
  { name: "Oral Formulation", id: "OF" },
  { name: "Botulinum Products and Therapeutics", id: "BPT" },
  { name: "Administrative Expenses", id: "AE" },
  { name: "Construction/Facility Expenses", id: "CFE" },
  { name: "Custom", id: "custom" },
];
 



const DOT_PALETTE = ["bg-indigo-500", "bg-sky-500", "bg-emerald-500", "bg-amber-500", "bg-rose-500", "bg-teal-500"];
export const dotFor = (name = "") => {
    const hash = Array.from(name).reduce((acc, ch) => acc + ch.charCodeAt(0), 0);
    return DOT_PALETTE[hash % DOT_PALETTE.length];
};

export const rowExpiryLevel = (row) => {
    const expireDate = new Date(row.expireDate);
    const now = new Date();
    const daysLeft = (expireDate - now) / 86400000;
    if (daysLeft < 0) return "expired";
    if (daysLeft <= 30) return "due";
    return "ok";
};

const EXPIRY_BADGE_CLASS = {
    expired: "bg-rose-50 text-rose-700 ring-rose-200",
    due: "bg-amber-50 text-amber-700 ring-amber-200",
};
export const ExpiryBadge = ({ level }) => {
    if (level === "ok") return null;
    return (
        <span className={`inline-flex w-fit shrink-0 items-center whitespace-nowrap rounded px-1.5 py-0.5 text-[11px] font-medium ring-1 ring-inset ${EXPIRY_BADGE_CLASS[level]}`}>
            {level === "expired" ? "Expired" : "Expiring soon"}
        </span>
    );
};

export const groupExpiryBadge = (rows) => {
    if (!rows?.length) return null;
    if (rows.some((r) => rowExpiryLevel(r) === "expired")) return <ExpiryBadge level="expired" />;
    if (rows.some((r) => rowExpiryLevel(r) === "due")) return <ExpiryBadge level="due" />;
    return null;
};

export const DOC_LABELS = {
  quotation: "Quotation Document",
  po: "Purchase Order Document",
  msds: "MSDS Document",
  coa: "CoA Document",
};


export const LOCATIONS = [
  "4° Refrigerator - Cell Culture Room 1",
  "4° Refrigerator - Cell Culture Room 2",
  "4° Refrigerator - Protein Purification Room",
  "4° Refrigerator - Molecular Biology Room",
  "-20° Freezer 1",
  "-20° Freezer 2",
  "-80° Freezer 1",
  "-80° Freezer 2",
  "Store Room",
  "Cabinet - Cell Culture Room 1",
  "Cabinet - Cell Culture Room 2",
  "Cabinet - Protein Purification Room",
  "Cabinet - Molecular Biology Room",
  "Cabinet - Bacterial Culture Room",
  "Cabinet - Lab 101",
  "Cabinet - Lab 102",
  "Lab 101",
  "Lab 102",
];



export const PROJECT_DATA = [
  { name: "Therapeutic Antibody", id: "MA-23" },
  { name: "Bispecific Antibody", id: "MA-23-25" },
  { name: "Spinal Cord Injury", id: "SCI-23" },
  { name: "Cancer Treatment", id: "CT-23" },
  { name: "Holistic Treatment", id: "CT-23-24A" },
  { name: "CAR-T Cell", id: "CT-23-24B" },
  { name: "Viral Engineering", id: "CT-23-24C" },
  { name: "Ligand based CAR-T Cell", id: "CT-23-25" },
  { name: "Mammalian Expression System", id: "MES-23" },
  { name: "Recombinant Vector", id: "MES-23A" },
  { name: "CHO Cells", id: "MES-23B" },
  { name: "Vaccine Design", id: "CT" },
  { name: "Neuronal Degeneration and Regeneration", id: "NDR" },
  { name: "Drug Delivery", id: "DD" },
  { name: "Topical Formulation", id: "TF" },
  { name: "Oral Formulation", id: "OF" },
  { name: "Botulinum Products and Therapeutics", id: "BPT" },
  { name: "Administrative Expenses", id: "AE" },
  { name: "Construction/Facility Expenses", id: "CFE" },
  { name: "Custom", id: "custom" },
];



export const STATUS_COLORS = {
     new: '#3b82f6',
    approved: '#10b981',
    poMade: '#8b5cf6',
    ordered: '#0ea5e9',
};

export const CHART_PALETTE = ['#4f46e5', '#0ea5e9', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#14b8a6', '#ec4899'];


export const ACCENTS = [
  { bg: "#E3F1EC", text: "#1E7F6C" }, 
  { bg: "#F5E8DC", text: "#B4652F" }, 
  { bg: "#F5E3DE", text: "#B14C39" }, 
  { bg: "#E1EBF0", text: "#39647F" }, 
  { bg: "#EBF0DD", text: "#748A2E" }, 
];

export const HELPER_TOOLS = [
  {
    id: "nebcutter",
    name: "NEBcutter",
    task: "Restriction site mapper",
    url: "https://nc3.neb.com/NEBcutter/",
    icon: FaCut,
    embeddable: true,
  },
  {
    id: "nebcloner",
    name: "NEBuilder Cloner",
    task: "Assembly design tool",
    url: "https://nebcloner.neb.com/#!/",
    icon: FaDna,
    embeddable: false,
  },
  {
    id: "tmcalculator",
    name: "Tm Calculator",
    task: "Primer melting temperature",
    url: "https://tmcalculator.neb.com/#!/main",
    icon: FaThermometerHalf,
    embeddable: false,
  },
  {
    id: "nebiocalculator",
    name: "NEBioCalculator",
    task: "Ligation ratio calculator",
    url: "https://nebiocalculator.neb.com/#!/ligation",
    icon: FaLink,
    embeddable: false,
  },
  {
    id: "dnaflip",
    name: "DNA Flip",
    task: "Reverse complement viewer",
    url: "https://rintze.zelle.me/dna-flip/",
    icon: FaExchangeAlt,
    embeddable: true,
  },
];

export const PROTEIN_TOOLS = [
  {
    id: "expasy",
    name: "Expasy",
    task: "SIB bioinformatics portal",
    url: "https://www.expasy.org/",
    icon: FaFlask,
    embeddable: false,
  },
  {
    id: "proteinatlas",
    name: "Human Protein Atlas",
    task: "Protein expression atlas",
    url: "https://www.proteinatlas.org/",
    icon: FaAtlas,
    embeddable: false,
  },
  {
    id: "alphafold",
    name: "AlphaFold DB",
    task: "Predicted 3D structures",
    url: "https://alphafold.ebi.ac.uk/",
    icon: FaCubes,
    embeddable: false,
  },
  {
    id: "biopython",
    name: "Biopython",
    task: "Python bioinformatics library",
    url: "https://biopython.org/",
    icon: FaCode,
    embeddable: false,
  },
  {
    id: "blast",
    name: "NCBI BLAST",
    task: "Sequence similarity search",
    url: "https://blast.ncbi.nlm.nih.gov/Blast.cgi",
    icon: FaSearch,
    embeddable: false,
  },
  {
    id: "vmd",
    name: "VMD",
    task: "Molecular visualization",
    url: "https://www.ks.uiuc.edu/Research/vmd/",
    icon: FaProjectDiagram,
    embeddable: false,
  },
  {
    id: "trex",
    name: "T-REX",
    task: "Phylogenetic tree inference",
    url: "https://bio.tools/t-rex",
    icon: FaTree,
    embeddable: false,
  },
  {
    id: "svmtrip",
    name: "SVMTriP",
    task: "Linear B-cell epitope prediction",
    url: "https://sysbio.unl.edu/SVMTriP/",
    icon: FaShieldAlt,
    embeddable: false,
  },
  {
    id: "svmtrip-biotools",
    name: "SVMTriP (bio.tools)",
    task: "Registry entry & docs",
    url: "https://bio.tools/svmtrip",
    icon: FaBookOpen,
    embeddable: false,
  },
];

export const CELL_TOOLS = [];

export const TOOL_CATEGORIES = [
  {
    id: "molecular",
    label: "Molecular biology",
    title: "Molecular biology toolkit",
    icon: FaDna,
    tools: HELPER_TOOLS,
  },
  {
    id: "protein",
    label: "Protein analysis",
    title: "Protein analysis toolkit",
    icon: FaCubes,
    tools: PROTEIN_TOOLS,
  },
  {
    id: "cell",
    label: "Cell analysis",
    title: "Cell analysis toolkit",
    icon: FaMicroscope,
    tools: CELL_TOOLS,
  },
];


// --------------------- Regex validation -----------------------
export const PHONE_REGEX = /^[6-9]\d{9}$/;
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;